import React, { useEffect, useMemo, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import { useAuthState } from '@bamboobox/b2logincheck';
import { BambooBoxIcon, DefaultCardHeaderIcon, ContactCardHeaderIcon } from '../../../assets/svg';
import useLookBackWindow from '../../../helpers/lookBackWindow.helper';
import { formateDateNew } from '../../../helpers/permissionForCards.helper';
import { Loader } from '../../../assets/icons/FallBackComponents';
import InfluencedChart from './influenced/InfluencedChart';
import {
  aggregate, aggregatePo, mergeChannels, valuesFor, sum, periodsFor, previousPeriod, delta, fmt, fmtMoney, shortLabel,
  monthLabel, monthIndexOfDate, rangeLabel, periodBounds, presetRanges,
} from './influenced/influencedData';
import './influenced/influenced.css';

const STAGES = [
  { k: 'po', name: 'Potential Opportunity', code: 'PO', unit: 'accounts', axis: 'Number of accounts', total: 'accounts' },
  { k: 'op', name: 'Opportunity', code: 'OPP', unit: 'opportunities', axis: 'Number of opportunities', total: 'opportunities' },
];
const stageOf = (k) => STAGES.find((s) => s.k === k);


const HISTORY_MONTHS = 24; // how far back the date picker goes (also capped by the tenant lookback window)

const CHECK = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
);
const CHEV = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
);

const InfluencedTrend = (props) => {
  const controllerRef = useRef();
  const poControllerRef = useRef();
  const rootRef = useRef();
  const toastTimer = useRef();
  const today = useMemo(() => new Date(), []);
  const nowMi = monthIndexOfDate(today);

  const userDetails = useAuthState();
  const tenantId = userDetails.user.tenantId;
  const lookbackDate = useLookBackWindow(tenantId, 'b2-marketing-influenced');
  const isOHRoutingPermissionPresent = userDetails?.permissions
    ?.filter((res) => res?.resourceName === 'unified_account_profile')?.[0]?.['operations']
    ?.some((ops) => ops === 'opportunityHub');

  const minMi = Math.max(nowMi - (HISTORY_MONTHS - 1), lookbackDate ? monthIndexOfDate(new Date(lookbackDate)) : -Infinity);

  const [gran, setGran] = useState('month');
  const [range, setRange] = useState({ a: nowMi - 2, b: nowMi });
  const [sel, setSel] = useState(null); // index of the single period shown, null = all periods
  const [stage, setStage] = useState('po'); // metric card driving the chart: 'po' | 'op'
  const [measure, setMeasure] = useState('count');
  const [hidden, setHidden] = useState({});
  const [open, setOpen] = useState(null); // 'date' | 'month' | null
  const [draft, setDraft] = useState(null);
  const [tip, setTip] = useState(null);
  const [toastMsg, setToastMsg] = useState('');
  const [spin, setSpin] = useState(0);

  const [apiMsg, setApiMsg] = useState([]);
  const [poApiMsg, setPoApiMsg] = useState([]);
  const [isPoLoading, setIsPoLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(Date.now());
  const [showValue, setShowValue] = useState(false); // tenant config: showInfluencedRevenue

  const toast = (msg) => {
    setToastMsg(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(''), 2200);
  };

  // ---------- data ----------
  useEffect(() => {
    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    const lookbackDateFilter = {
      attribute: 'opportunityCreatedAt',
      relation: 'between',
      type: 'opportunities',
      filterType: 'DATE',
      values: [lookbackDate, new Date().toISOString()],
    };
    const body = lookbackDate ? [...props.requestBody, lookbackDateFilter] : props.requestBody;

    if (props.icpSelected !== null && props.segmentName !== '') {
      setIsLoading(true);
      fetch(
        `${window._env_.UNIFIED_URL}/analytics/opportunities/${tenantId}/${props.icpSelected}/${props.segmentName}?filterName=${encodeURIComponent(props.segmentName)}&influenced=true`,
        {
          method: 'POST',
          headers: { 'Access-Control-Allow-Origin': '*', 'Content-Type': 'application/json' },
          body: JSON.stringify({ filters: body }),
          signal: controller.signal,
          credentials: 'include',
        }
      )
        .then((response) => response.json())
        .then((data) => {
          setApiMsg(Array.isArray(data?.msg) ? data.msg : []);
          setUpdatedAt(Date.now());
          setIsLoading(false);
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return; // a newer request owns the loading state
          setApiMsg([]);
          setIsLoading(false);
        });
    }
    return () => controller.abort();
  }, [props.requestBody, props.segmentName, props.icpSelected, props.softRefresh, props.refresh, lookbackDate]);

  useEffect(() => {
    if (poControllerRef.current) poControllerRef.current.abort();
    const controller = new AbortController();
    poControllerRef.current = controller;

    if (props.icpSelected && props.segmentName) {
      setIsPoLoading(true);
      const lookback = lookbackDate ? `&lookbackDate=${encodeURIComponent(new Date(lookbackDate).toISOString())}` : '';
      fetch(
        `${window._env_.UNIFIED_URL}/analytics/po-influenced/${tenantId}/${props.icpSelected}/${props.segmentName}?filterName=${encodeURIComponent(props.segmentName)}${lookback}`,
        {
          method: 'POST',
          headers: { 'Access-Control-Allow-Origin': '*', 'Content-Type': 'application/json' },
          body: JSON.stringify({ filters: props.requestBody }),
          signal: controller.signal,
          credentials: 'include',
        }
      )
        .then((response) => response.json())
        .then((data) => {
          setPoApiMsg(Array.isArray(data?.msg) ? data.msg : []);
          setIsPoLoading(false);
        })
        .catch((error) => {
          if (error?.name === 'AbortError') return;
          setPoApiMsg([]);
          setIsPoLoading(false);
        });
    } else {
      setIsPoLoading(false);
    }
    return () => controller.abort();
  }, [props.requestBody, props.segmentName, props.icpSelected, props.softRefresh, props.refresh, lookbackDate]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${window._env_.TENANT_CONFIG}/tenant/get-tenant-config-json`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ tenantId, tool: 'marketing_sourced_scheduler', keys: ['details'] }),
        });
        const data = await res.json();
        if (data.success) setShowValue(!!data?.data?.configs?.[0]?.configVal?.showInfluencedRevenue);
      } catch (e) {
        setShowValue(false);
      }
    };
    load();
  }, [tenantId]);

  // The lookback window arrives asynchronously; keep the selected range inside it.
  useEffect(() => {
    setRange((r) => (r.a < minMi ? { a: minMi, b: Math.max(r.b, minMi) } : r));
    setSel(null);
  }, [minMi]);

  // close popovers on outside click / Escape
  useEffect(() => {
    const onDown = (e) => {
      if (open && !(e.target.closest && e.target.closest('.mif-popwrap'))) { setOpen(null); setDraft(null); }
    };
    const onKey = (e) => {
      if (e.key === 'Escape' && open) { setOpen(null); setDraft(null); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  // ---------- derived ----------
  const { monthly, channels: oppChannels } = useMemo(() => aggregate(apiMsg), [apiMsg]);
  const po = useMemo(() => aggregatePo(poApiMsg, props.icpSelected), [poApiMsg, props.icpSelected]);
  const channels = useMemo(() => mergeChannels(oppChannels, po.rawByLabel), [oppChannels, po]);
  const periods = useMemo(() => periodsFor(range.a, range.b, gran), [range, gran]);
  const st = stageOf(stage);
  const valueMode = measure === 'value' && showValue; // the value measure only exists for opportunities
  const isValueFor = (k) => k === 'op' && valueMode;
  const monthlyOf = (k) => (k === 'po' ? po.monthly : monthly);
  const valsOf = (k, months, hide = hidden) => valuesFor(monthlyOf(k), channels, months, isValueFor(k) ? 'value' : 'count', hide);
  const isValue = isValueFor(stage);
  const vals = (months) => valsOf(stage, months);

  const buckets = sel === null ? periods.map((p, i) => ({ ...p, click: i })) : [{ ...periods[sel], click: null }];
  const bars = buckets.map((b, i) => {
    const v = vals(b.months);
    let d = null;
    if (sel === null && i > 0) {
      const dd = delta(sum(v), sum(vals(buckets[i - 1].months)));
      d = { c: dd.c, text: buckets.length > 5 ? dd.t : `${dd.t} vs ${shortLabel(buckets[i - 1].label)}` };
    }
    return { label: b.label, months: b.months, vals: v, delta: d, clickable: b.click !== null, click: b.click };
  });

  let prev = sel === null ? null : previousPeriod(periods, sel, gran);
  if (prev && prev.months[0] < minMi) prev = null;
  const tileFor = (k) => {
    const total = sum(buckets.map((b) => sum(valsOf(k, b.months))));
    if (!prev) return { total, delta: { text: ' ', c: '' } };
    const dd = delta(total, sum(valsOf(k, prev.months)));
    return { total, delta: { text: `${dd.t} vs ${shortLabel(prev.label)}`, c: dd.c } };
  };
  const hasData = bars.some((b) => sum(b.vals) > 0);
  const hiddenCount = channels.filter((_, i) => hidden[i]).length;

  // ---------- actions ----------
  const pick = (i) => { setSel(i); setOpen(null); setTip(null); };

  const applyRange = (a, b) => { setRange({ a, b }); setSel(null); setOpen(null); setDraft(null); };

  const opportunityHubLink = (bar, ci) => {
    const { start, end } = periodBounds(bar.months, today);
    const ch = channels[ci];
    const named = ch.raw.filter((r) => r !== null && r !== 'null' && r !== '');
    const channelFilter = {
      attrId: nanoid(), attribute: 'opportunityInfluencedChannel', relation: 'includes', type: 'OPPORTUNITY', values: named,
    };
    if (named.length === 0) { channelFilter.relation = 'is empty'; channelFilter.values = []; }
    const filter = [
      { attrId: nanoid(), attribute: 'opportunityCreatedAt', relation: 'between', type: 'OPPORTUNITY', values: [start, end] },
      channelFilter,
    ];
    const enc = (o) => encodeURIComponent(encodeURIComponent(JSON.stringify(o)));
    return `${window._env_.UNIFIED_FRONTEND_URL}/#/filter/${enc([...filter, ...props.requestBody])}/analytics_dashboard/${enc(props.requestBody)}/?icp=${props.icpSelected}&segmentName=${props.segmentName}&newDashboard=true&tabVal=${props.tabVal}&tab=opportunity&category=${props?.category}`;
  };

  // Accounts Hub filtered to the accounts behind a PO bar segment. The backend resolves the accounts
  // (poInfluencedChannel filter), so the link stays small and covers every account the bar counted.
  const accountsHubLink = (bar, ci) => {
    const { start, end } = periodBounds(bar.months, today);
    const raws = channels[ci].raw.map((r) => (r === null || r === undefined ? 'null' : String(r)));
    const filter = [{
      attrId: nanoid(),
      attribute: 'poInfluencedChannel',
      relation: 'includes',
      type: 'ACCOUNTS',
      values: raws.length ? raws : [channels[ci].label],
      range: [start, end],
      icp: props.icpSelected,
      lookbackDate: lookbackDate ? new Date(lookbackDate).toISOString() : null,
    }];
    const enc = (o) => encodeURIComponent(encodeURIComponent(JSON.stringify(o)));
    return `${window._env_.UNIFIED_FRONTEND_URL}/#/filter/${enc([...filter, ...props.requestBody])}/analytics_dashboard/${enc(props.requestBody)}/?icp=${props.icpSelected}&segmentName=${props.segmentName}&newDashboard=true&tabVal=${props.tabVal}&category=${props?.category}`;
  };

  const pickStage = (k) => {
    if (k === stage) { toast('Keep at least one metric selected'); return; }
    setStage(k); setTip(null);
  };

  const onSegmentClick = (i, ci) => {
    setTip(null);
    if (stage === 'po') { window.open(accountsHubLink(bars[i], ci), '_self'); return; }
    if (isOHRoutingPermissionPresent) window.open(opportunityHubLink(bars[i], ci), '_self');
    else if (bars[i].clickable) pick(bars[i].click);
  };

  const onSegmentHover = (e, i, ci) => setTip({ x: e.clientX, y: e.clientY, i, ci });

  const toggleChannel = (i) => {
    if (!hidden[i] && hiddenCount === channels.length - 1) { toast('Keep at least one channel visible'); return; }
    setHidden((h) => ({ ...h, [i]: !h[i] }));
  };

  const refresh = () => {
    setSel(null); setOpen(null); setSpin((s) => s + 1);
    if (props.setSoftRefresh) props.setSoftRefresh((s) => !s);
    toast('Data refreshed');
  };

  // ---------- render pieces ----------
  const presets = presetRanges(nowMi).map((p) => ({ ...p, a: Math.max(p.a, minMi) }));
  const monthOptions = [];
  for (let mi = minMi; mi <= nowMi; mi++) monthOptions.push(mi);
  const dr = draft || range;

  const renderTip = () => {
    if (!tip || !bars[tip.i]) return null;
    const bar = bars[tip.i];
    const isOpp = stage === 'op';
    const cnt = valuesFor(monthlyOf(stage), channels, bar.months, 'count', null);
    const val = isOpp ? valuesFor(monthly, channels, bar.months, 'value', null) : null;
    let tc = 0, tv = 0;
    const rows = channels.map((ch, i) => {
      const off = !!hidden[i];
      if (!off) { tc += cnt[i]; if (val) tv += val[i]; }
      return (
        <div key={ch.label} className={`r${i === tip.ci ? ' hl' : ''}${off ? ' off' : ''}`}>
          <span className="d" style={{ background: ch.color, opacity: off ? 0.3 : 1 }} />
          {ch.label}
          <b>{off ? 'hidden' : isOpp && showValue ? `${cnt[i]} · ${fmtMoney(val[i])}` : cnt[i]}</b>
        </div>
      );
    });
    const w = 260, h = 40 + channels.length * 22;
    const left = tip.x + 16 + w > window.innerWidth - 8 ? tip.x - w - 16 : tip.x + 16;
    const top = Math.min(tip.y + 14, window.innerHeight - h - 8);
    return (
      <div className="mif-tip" role="tooltip" style={{ left: Math.max(8, left), top: Math.max(8, top) }}>
        <h4>{st.name} · {bar.label}</h4>
        {rows}
        <div className="t">Total {st.total}<b>{isOpp && showValue ? `${fmt(tc)} · ${fmtMoney(tv)}` : fmt(tc)}</b></div>
      </div>
    );
  };

  return (
    <div className="mif" ref={rootRef} aria-label="Marketing-influenced funnel">
      <div className="mif-hd">
        <div className="mif-ttl">
          {props.cardDetails?.cardObject?.toLowerCase() === 'account' ? <DefaultCardHeaderIcon /> : <ContactCardHeaderIcon />}
          <h1>{props.cardDetails?.title || 'Marketing-Influenced Funnel'}</h1>
          <span className="mif-infow">
            <button className="mif-info" aria-describedby="mif-itip" aria-label="About this chart">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="9.5" /><path d="M12 11v6M12 7.5v.01" strokeLinecap="round" /></svg>
            </button>
            <span className="mif-itip" id="mif-itip" role="tooltip">
              This helps you understand how channels contribute to potential opportunities and to the opportunities they eventually convert into. Potential opportunities are counted as accounts, shared equally across the channels that influenced them. Opportunities are counted separately.
            </span>
          </span>
        </div>
        <div className="mif-hr">
          <div className="mif-tabs" role="tablist" aria-label="Granularity">
            {['month', 'quarter'].map((g) => (
              <button key={g} className="mif-tab" role="tab" aria-selected={gran === g}
                onClick={() => { setGran(g); setSel(null); setOpen(null); }}>{g === 'month' ? 'Monthly' : 'Quarterly'}</button>
            ))}
          </div>
          <div className="mif-popwrap">
            <button className="mif-pill" aria-haspopup="dialog" aria-expanded={open === 'date'}
              onClick={() => { setOpen(open === 'date' ? null : 'date'); setDraft(open === 'date' ? null : { ...range }); }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15" rx="2" /><path d="M3.5 10h17M8 3v4M16 3v4" /></svg>
              {rangeLabel(range.a, range.b, today)}{CHEV}
            </button>
            {open === 'date' && (
              <div className="mif-pop r mif-dpop" role="dialog" aria-label="Date range">
                <div className="mif-pl">Quick ranges</div>
                {presets.map((p) => (
                  <button key={p.id} className="mif-opt" aria-pressed={range.a === p.a && range.b === p.b} onClick={() => applyRange(p.a, p.b)}>{p.label}{CHECK}</button>
                ))}
                <div className="mif-pl">Custom range</div>
                <div className="mif-cust">
                  <label>From
                    <select value={dr.a} onChange={(e) => setDraft({ ...dr, a: +e.target.value })}>
                      {monthOptions.map((mi) => <option key={mi} value={mi}>{monthLabel(mi)}</option>)}
                    </select>
                  </label>
                  <label>To
                    <select value={dr.b} onChange={(e) => setDraft({ ...dr, b: +e.target.value })}>
                      {monthOptions.map((mi) => <option key={mi} value={mi}>{monthLabel(mi)}</option>)}
                    </select>
                  </label>
                </div>
                <div className="mif-pf">
                  <span className="mif-err">{dr.a <= dr.b ? '' : 'Start must be before end'}</span>
                  <span className="mif-grp">
                    <button className="mif-btn ghost" onClick={() => { setOpen(null); setDraft(null); }}>Cancel</button>
                    <button className="mif-btn" disabled={dr.a > dr.b} onClick={() => applyRange(dr.a, dr.b)}>Apply</button>
                  </span>
                </div>
              </div>
            )}
          </div>
          <button key={spin} className={`mif-ib${spin ? ' mif-spin' : ''}`} aria-label="Refresh data" onClick={refresh}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.4-5.7M20 4v5h-5" /></svg>
          </button>
        </div>
      </div>

      <div className="mif-mcs" role="group" aria-label="Metrics">
        {STAGES.map((m) => {
          const on = stage === m.k;
          const t = tileFor(m.k);
          const isV = isValueFor(m.k);
          return (
            <div key={m.k} className={`mif-mc${on ? ' on' : ''}`}>
              <button className="mif-mc-main" aria-pressed={on} onClick={() => pickStage(m.k)}>
                <span className="mif-mc-top">
                  <span className="mif-ov">{m.name}</span>
                  <span className="mif-ck"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg></span>
                </span>
                <span className="mif-big">{isV ? fmtMoney(t.total) : fmt(t.total)}<span className="mif-unit">{isV ? 'pipeline' : m.unit}</span></span>
                <span className={`mif-dl ${t.delta.c}`}>{t.delta.text}</span>
              </button>
            </div>
          );
        })}
      </div>

      {showValue && stage === 'op' && (
        <div className="mif-ctl">
          <div className="mif-seg" role="group" aria-label="Measure">
            <button aria-pressed={!isValue} onClick={() => setMeasure('count')}>Number of opportunities</button>
            <button aria-pressed={isValue} onClick={() => setMeasure('value')}>Pipeline value</button>
          </div>
        </div>
      )}

      <div>
        {(stage === 'op' ? isLoading : isPoLoading) ? (
          <div className="mif-load"><Loader /></div>
        ) : !hasData ? (
          <div className="mif-empty">No data to display</div>
        ) : (
          <div className="mif-tc">
            <InfluencedChart
              bars={bars}
              channels={channels}
              isValue={isValue}
              ariaLabel={`${st.name} ${sel === null ? `by ${gran}` : `for ${periods[sel].label}`}, stacked by channel`}
              canDrill={stage === 'po' || !!isOHRoutingPermissionPresent}
              unitLabel={st.axis}
              stageCode={st.code}
              onPickBar={(i) => pick(bars[i].click)}
              onSegmentClick={onSegmentClick}
              onSegmentHover={onSegmentHover}
              onSegmentLeave={() => setTip(null)}
            />
          </div>
        )}
      </div>

      <div className="mif-leg">
        <div className="mif-tm">TIME</div>
        <div className="mif-fl">
          {channels.map((ch, i) => (
            <button key={ch.label} className={`mif-fi${hidden[i] ? ' off' : ''}`} aria-pressed={!hidden[i]} onClick={() => toggleChannel(i)}>
              <i style={{ background: ch.color }} />{ch.label}
            </button>
          ))}
        </div>
        <div className="mif-key"><b>{st.code}</b> {st.name}</div>
      </div>

      <div className="mif-ftr">
        <button className="mif-logo" aria-describedby="mif-ftip" aria-label="About this card"><BambooBoxIcon /></button>
        <div className="mif-ftip" id="mif-ftip" role="tooltip">
          Created by: {props.cardDetails?.createdBy}<br />
          Created at: {formateDateNew(props.cardDetails?.createdAt)}
        </div>
        <span className="mif-upd">Last Updated {formateDateNew(updatedAt)}</span>
      </div>

      {renderTip()}
      <div className={`mif-toast${toastMsg ? ' on' : ''}`} role="status" aria-live="polite">{toastMsg}</div>
    </div>
  );
};

export default InfluencedTrend;
